import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { isPersistentEditCandidateEditable } from "@/payroll/ai-script/ai-script-persistent-edits-utils";
import {
  useDisambiguateAiScriptCellOverride,
  useGetAiScriptOverrideCandidates,
} from "@/payroll/ai-script/query";
import type {
  IAiSpreadsheetCellLocation,
  IAiSpreadsheetCellOverride,
  IAiSpreadsheetSheet,
} from "@/payroll/ai-script/types";
import { RowPickerCandidate } from "./row-picker-candidate";
import {
  LoadError,
  PersistentEditValue,
  SectionLabel,
  showPersistentEditMutationError,
} from "./shared";

interface RowPickerProps {
  override: IAiSpreadsheetCellOverride;
  businessId: string | null;
  runId: string;
  sheets: Pick<IAiSpreadsheetSheet, "id" | "editable">[];
  onGoToCell: (cell: IAiSpreadsheetCellLocation) => void;
  onResolved: () => void;
  onWorkbookMutated?: () => void;
}

export const RowPicker = ({
  override,
  businessId,
  runId,
  sheets,
  onGoToCell,
  onResolved,
  onWorkbookMutated,
}: RowPickerProps) => {
  const { data, isLoading, isError, refetch } =
    useGetAiScriptOverrideCandidates(businessId, runId, override.id);
  const disambiguate = useDisambiguateAiScriptCellOverride(businessId);
  const candidates = data?.candidates ?? [];

  return (
    <Stack gap="sm" fullWidth>
      <SectionLabel>Which row is this edit for?</SectionLabel>
      <Typography variant="bodyW3" textColor="secondary">
        Your edit (
        <PersistentEditValue value={override.value?.computed_value} />) matched
        more than one row. Pick the one it belongs to.
      </Typography>
      {isLoading ? (
        <Typography variant="bodyW3" textColor="secondary">
          Loading rows…
        </Typography>
      ) : isError ? (
        <LoadError
          message="Couldn't load the matching rows."
          onRetry={() => void refetch()}
        />
      ) : candidates.length === 0 ? (
        <Typography variant="bodyW3" textColor="secondary">
          None of the matching rows are on this run.
        </Typography>
      ) : (
        <Stack gap="sm" fullWidth>
          {candidates.map((candidate) => {
            const isEditable = isPersistentEditCandidateEditable(
              candidate.sheet_id,
              sheets,
            );
            const isSelectedCandidate =
              disambiguate.isPending &&
              disambiguate.variables?.cellId === candidate.cell_id;

            return (
              <RowPickerCandidate
                key={candidate.cell_id}
                candidate={candidate}
                isEditable={isEditable}
                isSelected={isSelectedCandidate}
                isPending={disambiguate.isPending}
                onView={() => onGoToCell(candidate)}
                onSelect={() =>
                  disambiguate.mutate(
                    {
                      runId,
                      overrideId: override.id,
                      cellId: candidate.cell_id,
                    },
                    {
                      onSuccess: () => {
                        onWorkbookMutated?.();
                        onResolved();
                      },
                      onError: (error) => {
                        if (
                          showPersistentEditMutationError(
                            "Couldn't apply the edit to that row",
                            error,
                          )
                        ) {
                          onResolved();
                        }
                      },
                    },
                  )
                }
              />
            );
          })}
        </Stack>
      )}
    </Stack>
  );
};
